import { useEffect, useState } from 'react';
import {
	BooleanField,
	Create,
	Edit,
	SelectInput,
	Show,
	SimpleForm,
	SimpleShowLayout,
	TextField,
	TextInput,
	required,
	useDataProvider,
	useGetList,
	useNotify,
	useRecordContext,
	useRedirect
} from 'react-admin';
import { Link as RouterLink } from 'react-router-dom';
import {
	Box,
	Button,
	CircularProgress,
	MenuItem,
	Select,
	Skeleton,
	Stack,
	Tab,
	Tabs,
	TextField as MuiTextField,
	Typography
} from '@mui/material';
import { default as icon } from '@mui/icons-material/Article';
import { globalPromptChoices } from '../promptKeys';
import PromptTextBlock from '../components/PromptTextBlock';

function versionStatusLabel( version ) {
	const status = version.status || ( version.active ? 'active' : 'draft' );
	return [ 'active', 'draft' ].includes( status ) ? ` (${status})` : '';
}

const ReadOnlyBlock = ({ label, body }) => (
	<PromptTextBlock label={label} text={body} minHeight={240} maxHeight={560} />
);

const PromptTextShowField = ({ label, source, minHeight }) => {
	const record = useRecordContext();
	return (
		<PromptTextBlock
			label={label}
			text={record?.[source] || ''}
			minHeight={minHeight}
			maxHeight={560}
			sx={{ width: '100%' }}
		/>
	);
};

function activeRecordForKey( templates, promptKey ) {
	return templates.find( template => template.prompt_key === promptKey && Number( template.active ) === 1 )
		|| templates.find( template => template.prompt_key === promptKey )
		|| null;
}

const PromptTemplateList = () => {
	const { data = [], isLoading, isFetching } = useGetList(
		'prompt-templates',
		{
			pagination: { page: 1, perPage: 100 },
			sort: { field: 'prompt_key', order: 'ASC' }
		}
	);
	const existingKeys = new Set( data.map( template => template.prompt_key ) );
	const slots = globalPromptChoices.filter( choice => existingKeys.has( choice.id ) );
	const hasUnusedSlots = globalPromptChoices.some( choice => !existingKeys.has( choice.id ) );
	const [ selectedKey, setSelectedKey ] = useState( '' );
	const activeKey = slots.some( slot => slot.id === selectedKey ) ? selectedKey : slots[0]?.id || '';
	const activeRecord = activeRecordForKey( data, activeKey );

	useEffect(() => {
		if ( activeKey && activeKey !== selectedKey ) {
			setSelectedKey( activeKey );
		}
	}, [ activeKey, selectedKey ]);

	return (
		<Box sx={{ width: '100%' }}>
			<Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
				<Typography variant="h5">Prompt Templates</Typography>
				{isFetching && !isLoading && <CircularProgress size={18} />}
			</Stack>
			{isLoading ? (
				<>
					<Skeleton variant="rectangular" height={48} sx={{ mb: 2 }} />
					<PromptTemplateEditorSkeleton />
				</>
			) : (
				<>
			<Tabs
				value={activeKey || '__create__'}
				onChange={(event, value) => setSelectedKey( value )}
				sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
			>
				{slots.map(slot => (
					<Tab key={slot.id} label={slot.name} value={slot.id} />
				))}
				{hasUnusedSlots && (
					<Tab
						label="+"
						value="__create__"
						component={RouterLink}
						to="/prompt-templates/create"
					/>
				)}
			</Tabs>
			{activeKey ? (
				<PromptTemplateVersionEditor
					promptKey={activeKey}
					initialRecord={activeRecord || { prompt_key: activeKey }}
					navigateOnVersionChange={false}
				/>
			) : (
				<Box sx={{ py: 4 }}>
					<Typography variant="body2" color="text.secondary">
						No prompt template slots exist yet.
					</Typography>
					<Button
						variant="contained"
						component={RouterLink}
						to="/prompt-templates/create"
						sx={{ mt: 2 }}
					>
						Create initial template
					</Button>
				</Box>
			)}
				</>
			)}
		</Box>
	);
};

const PromptTemplateKeyInput = () => {
	const { data = [] } = useGetList(
		'prompt-templates',
		{
			pagination: { page: 1, perPage: 100 },
			sort: { field: 'prompt_key', order: 'ASC' }
		}
	);
	const existingKeys = new Set( data.map( template => template.prompt_key ) );
	const availableChoices = globalPromptChoices.filter( choice => !existingKeys.has( choice.id ) );
	const allKeysExist = availableChoices.length === 0;

	return (
		<>
			<SelectInput
				source="prompt_key"
				choices={availableChoices}
				validate={required()}
				disabled={allKeysExist}
				helperText={allKeysExist ? 'All prompt template keys already exist. Create new versions from the existing templates.' : undefined}
			/>
		</>
	);
};

const PromptTemplateCreate = () => (
	<Create>
		<SimpleForm>
			<PromptTemplateKeyInput />
			<TextInput source="label" />
			<TextInput source="description" label="Description / Internal Notes" multiline fullWidth />
			<TextInput source="body" multiline fullWidth validate={required()} />
			<TextInput source="guidance" label="Module prompt guidance" multiline fullWidth />
		</SimpleForm>
	</Create>
);

const PromptTemplateVersionEditor = ({ promptKey, initialRecord, navigateOnVersionChange = true }) => {
	const contextRecord = useRecordContext();
	const record = initialRecord || contextRecord;
	const selectedPromptKey = promptKey || record?.prompt_key;
	const dataProvider = useDataProvider();
	const notify = useNotify();
	const redirect = useRedirect();
	const showPromptKeyField = !promptKey;
	const [ versions, setVersions ] = useState([]);
	const [ selectedId, setSelectedId ] = useState('');
	const [ label, setLabel ] = useState('');
	const [ description, setDescription ] = useState('');
	const [ body, setBody ] = useState('');
	const [ guidance, setGuidance ] = useState('');
	const [ loadingVersions, setLoadingVersions ] = useState( false );
	const selected = versions.find( version => String( version.id ) === String( selectedId ) );
	const isDraft = selected?.status === 'draft' && Number( selected?.active ) !== 1;
	const isReadOnly = !isDraft;
	const canActivate = selected && Number( selected.active ) !== 1;

	const applyVersion = ( version ) => {
		if ( !version ) {
			setSelectedId( '' );
			setLabel( '' );
			setDescription( '' );
			setBody( '' );
			setGuidance( '' );
			return;
		}

		setSelectedId( version.id );
		setLabel( version.label || '' );
		setDescription( version.description || '' );
		setBody( version.body || '' );
		setGuidance( version.guidance || '' );
	};

	const loadVersions = ( preferredId ) => {
		if ( !selectedPromptKey ) {
			return Promise.resolve();
		}

		setLoadingVersions( true );
		return dataProvider.getList( 'prompt-templates', {
			filter: { prompt_key: selectedPromptKey },
			pagination: { page: 1, perPage: 100 },
			sort: { field: 'updatedAt', order: 'DESC' }
		}).then(({ data }) => {
			setVersions( data );
			const next = data.find( version => String( version.id ) === String( preferredId || selectedId ) )
				|| data.find( version => String( version.id ) === String( record.id ) )
				|| data.find( version => Number( version.active ) === 1 )
				|| data[0];
			applyVersion( next );
		}).finally(() => setLoadingVersions( false ));
	};

	useEffect(() => {
		loadVersions().catch(error => notify( error.message || 'Could not load prompt template versions', { type: 'error' } ));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [ record?.id, selectedPromptKey ]);

	const selectVersion = event => {
		const id = event.target.value;
		const version = versions.find( item => String( item.id ) === String( id ) );
		applyVersion( version );
		if ( version && navigateOnVersionChange ) {
			redirect( 'edit', 'prompt-templates', version.id );
		}
	};

	const saveDraft = async () => {
		if ( !selected || !isDraft ) {
			return;
		}

		try {
			await dataProvider.update( 'prompt-templates', {
				id: selected.id,
				data: {
					...selected,
					label,
					description,
					guidance,
					body,
					status: 'draft',
					active: 0
				},
				previousData: selected
			});
			notify( 'Draft saved' );
			await loadVersions( selected.id );
		} catch ( error ) {
			notify( error.message || 'Draft save failed', { type: 'error' } );
		}
	};

	const createDraftFromSelected = async () => {
		const source = selected;
		if ( !source ) {
			return;
		}

		try {
			const { data } = await dataProvider.create( 'prompt-templates', {
				data: {
					prompt_key: source.prompt_key,
					label: `Draft from ${source.label || `version ${source.id}`}`,
					description: source.description,
					guidance: source.guidance,
					body: source.body,
					status: 'draft',
					active: 0
				}
			});
			notify( 'Draft created' );
			await loadVersions( data.id );
			if ( navigateOnVersionChange ) {
				redirect( 'edit', 'prompt-templates', data.id );
			}
		} catch ( error ) {
			notify( error.message || 'Draft creation failed', { type: 'error' } );
		}
	};

	const activateVersion = async () => {
		if ( !canActivate ) {
			return;
		}

		try {
			await dataProvider.update( 'prompt-templates', {
				id: selected.id,
				data: {
					...selected,
					label,
					description,
					guidance,
					body,
					status: 'active',
					active: 1
				},
				previousData: selected
			});
			notify( 'Prompt template activated' );
			await loadVersions( selected.id );
		} catch ( error ) {
			notify( error.message || 'Activation failed', { type: 'error' } );
		}
	};

	if ( !record ) {
		return null;
	}

	return (
		<Box sx={{ width: '100%' }}>
			<Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
				<Select
					size="small"
					value={selectedId}
					onChange={selectVersion}
					displayEmpty
					disabled={loadingVersions}
					sx={{ minWidth: 300 }}
				>
					<MenuItem value="">Select version</MenuItem>
					{versions.map(version => (
						<MenuItem key={version.id} value={version.id}>
							{version.label || `Version ${version.id}`}{versionStatusLabel( version )}
						</MenuItem>
					))}
				</Select>
				{loadingVersions && <CircularProgress size={22} />}
				{isReadOnly && <Button variant="outlined" onClick={createDraftFromSelected} disabled={loadingVersions}>Edit</Button>}
				{isReadOnly && canActivate && <Button variant="outlined" onClick={activateVersion} disabled={loadingVersions}>Activate Version</Button>}
				{isDraft && (
					<>
						<Button variant="contained" onClick={saveDraft} disabled={loadingVersions}>Save Draft</Button>
						<Button variant="outlined" onClick={activateVersion} disabled={loadingVersions}>Activate Version</Button>
					</>
				)}
			</Stack>
			{loadingVersions ? <PromptTemplateEditorSkeleton /> : <Stack spacing={2}>
				{showPromptKeyField && (
					<MuiTextField
						label="Prompt key"
						value={selected?.prompt_key || record.prompt_key || ''}
						disabled
						fullWidth
					/>
				)}
				{!isReadOnly && (
					<MuiTextField
						label="Version Label"
						value={label}
						onChange={event => setLabel( event.target.value )}
						fullWidth
					/>
				)}
				{isReadOnly ? (
					<Box>
						<Typography variant="caption" color="text.secondary">Description / Internal Notes</Typography>
						<Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{description || ''}</Typography>
					</Box>
				) : (
					<MuiTextField
						label="Description / Internal Notes"
						value={description}
						onChange={event => setDescription( event.target.value )}
						multiline
						minRows={2}
						fullWidth
					/>
				)}
				{isReadOnly ? (
					<ReadOnlyBlock label="Prompt Template" body={body} />
				) : (
					<MuiTextField
						label="Prompt Template"
						value={body}
						onChange={event => setBody( event.target.value )}
						multiline
						minRows={18}
						fullWidth
						required
					/>
				)}
				{isReadOnly ? (
					<ReadOnlyBlock label="Module prompt guidance" body={guidance} />
				) : (
					<MuiTextField
						label="Module prompt guidance"
						value={guidance}
						onChange={event => setGuidance( event.target.value )}
						multiline
						minRows={6}
						fullWidth
					/>
				)}
			</Stack>}
		</Box>
	);
};

const PromptTemplateEditorSkeleton = () => (
	<Stack spacing={2}>
		<Skeleton variant="rectangular" height={56} />
		<Skeleton variant="rectangular" height={56} />
		<Skeleton variant="rectangular" height={72} />
		<Skeleton variant="rectangular" height={360} />
		<Skeleton variant="rectangular" height={160} />
	</Stack>
);

const PromptTemplateEdit = () => (
	<Edit mutationMode="pessimistic">
		<PromptTemplateVersionEditor />
	</Edit>
);

const PromptTemplateShow = () => (
	<Show>
		<SimpleShowLayout>
			<TextField source="prompt_key" />
			<TextField source="label" />
			<TextField source="description" />
			<TextField source="status" />
			<BooleanField source="active" />
			<PromptTextShowField source="guidance" label="Module prompt guidance" />
			<PromptTextShowField source="body" label="Prompt Template" minHeight={240} />
		</SimpleShowLayout>
	</Show>
);

export const PromptTemplate = {
	list: PromptTemplateList,
	edit: PromptTemplateEdit,
	create: PromptTemplateCreate,
	show: PromptTemplateShow,
	icon
};

export default PromptTemplate;
